// @ts-strict-ignore
import type { ApolloClient } from "@apollo/client";
import { ApolloProvider, type NormalizedCacheObject } from "@apollo/client";
import { useEffect, type ReactNode } from "react";

import { useApolloErrorMessages } from "@shared/data/client/useApolloErrorMessages/useApolloErrorMessages";

import { useClient } from "./useClient";

interface BaseProps {
  children: ReactNode;
}

interface ClientProps extends BaseProps {
  serverSideClient?: never;
}

interface ServerProps extends BaseProps {
  serverSideClient: ApolloClient<NormalizedCacheObject>;
}

/**
 * An Apollo client provider that is to be used either server or client side.
 * If no serverSideClient is passed, it will assume that we are running on the browser and initialize an Apollo client.
 *
 * @param {ApolloClient<NormalizedCacheObject>} serverSideClient - Only required on the server side
 */
const ApolloClientProvider = ({ children, ...rest }: ClientProps | ServerProps) => {
  useApolloErrorMessages();
  const client = useClient();

  useEffect(() => {
    // re-enable network fetches after hydration
    // these need to be disabled intially in order to prevent client/server mismatches due to cache-and-network fetchPolicy queries
    // see app/javascript/remix/components/ApolloClientProvider/useClient.ts for more info
    // eslint-disable-next-line react-compiler/react-compiler
    client.disableNetworkFetches = false;
  }, []);

  return (
    <ApolloProvider client={"serverSideClient" in rest ? rest.serverSideClient : client}>
      {children}
    </ApolloProvider>
  );
};

export { ApolloClientProvider };
